import { useTranslation } from 'next-i18next';

import { useCallback, useEffect, useState } from 'react';

import {
    Box,
    Button,
    Flex,
    GridItem,
    HStack,
    Link,
    Menu,
    Separator,
    useMediaQuery,
    VStack,
} from '@chakra-ui/react';

import { urlHelpers } from '@nocowanie/core';
import { Avatar, GridWrapper, searchEngineIsMobileSsrBreakpoint } from '@nocowanie/common-ui';
import { IconsLinear } from '@nocowanie/icons';

import { SearchEngineWrapper } from '@app/components/molecules/search-engine-wrapper';
import { getDashboardLink, logOutUser } from '@app/helpers/navigation.helpers';
import { useUserProfile } from '@app/hooks';
import { NavGroupModel } from '@app/models';

import { AppHeaderLogo } from './app-header-logo';
import { AppHeaderOldProps } from './app-header-old.props';
import { OpenChangeDetails } from 'node_modules/@chakra-ui/react/dist/types/components/menu/namespace';

import staticData from '../../../assets/data/static-data.json';
import { useAppHeaderHook } from '../app-header/app-header.hook';
import { Navigation, NavigationMobile } from '../navigation';
import navLinks from '../navigation/navigation-links.json';

export const AppHeaderOld = ({ isSimplified = true }: AppHeaderOldProps) => {
    const { t } = useTranslation('common', {
        keyPrefix: 'appHeader',
    });
    const { isMobileRes } = useAppHeaderHook();
    const { user } = useUserProfile();
    const [isProfileOpen, setIsProfileOpen] = useState(false);

    const handleLogout = useCallback(() => {
        logOutUser();
    }, []);

    const onProfileChange = useCallback((details: OpenChangeDetails) => {
        setIsProfileOpen(details.open);
    }, []);

    useEffect(() => {
        if (!isProfileOpen) {
            return;
        }

        const onProfileClose = () => setIsProfileOpen(false);

        window.addEventListener('scroll', onProfileClose);

        return () => {
            window.removeEventListener('scroll', onProfileClose);
        };
    }, [isProfileOpen]);

    const links = navLinks as NavGroupModel[];

    const [isMobileSearchEngineVersion] = useMediaQuery(
        [`(max-width: ${searchEngineIsMobileSsrBreakpoint})`],
        {
            fallback: [!!isMobileRes],
        },
    );

    const HeaderRightSection = () =>
        !isMobileRes ? (
            user ? (
                <Menu.Root open={isProfileOpen} onOpenChange={onProfileChange}>
                    <Menu.Trigger
                        as={Button}
                        border={'none'}
                        _hover={{
                            bg: 'transparent',
                        }}
                        _expanded={{
                            bg: 'transparent',
                        }}
                        title={user.firstName || user.lastName || 'user'}
                    >
                        <Avatar src={user.avatar ?? undefined} />
                        <IconsLinear.ArrowDown2 fontSize={'sm'} />
                    </Menu.Trigger>
                    <Menu.Positioner>
                        <Menu.Content zIndex={5} color={'secondary.600'} fontSize={'sm'}>
                            <Menu.Item
                                asChild
                                _hover={{
                                    bg: 'secondary.50',
                                    textDecoration: 'none',
                                }}
                                value={'dashboard'}
                                cursor={'pointer'}
                            >
                                <a href={getDashboardLink(user)}>{t('userMenu.goToPanel')}</a>
                            </Menu.Item>
                            <Menu.Item
                                value={'logout'}
                                onClick={handleLogout}
                                _hover={{
                                    bg: 'secondary.50',
                                }}
                                cursor={'pointer'}
                            >
                                {t('userMenu.logOut')}
                            </Menu.Item>
                        </Menu.Content>
                    </Menu.Positioner>
                </Menu.Root>
            ) : (
                <HStack gap={'gutterWidth'}>
                    <VStack fontSize={'xs'} align={'flex-start'} gap={1}>
                        <Link
                            href={urlHelpers.getPhoneNoHref(staticData.navigation.hotline)}
                            variant={'plain'}
                            colorPalette={'gray'}
                        >
                            <IconsLinear.Call mr={2} fontSize={'sm'} />
                            {staticData.navigation.hotline}
                        </Link>
                        <Link
                            href={`mailto:${staticData.navigation.customerServiceEmail}`}
                            variant={'plain'}
                            colorPalette={'gray'}
                        >
                            <IconsLinear.Sms mr={2} fontSize={'sm'} />
                            {staticData.navigation.customerServiceEmail}
                        </Link>
                    </VStack>
                    <Separator
                        orientation={'vertical'}
                        borderColor={'gray.200'}
                        h={'auto'}
                        alignSelf={'stretch'}
                    />
                    <Link
                        variant={'plain'}
                        colorPalette={'gray'}
                        href={links[0].navLinks[0].url ?? '/'}
                    >
                        {t('cta.logIn')}
                    </Link>
                </HStack>
            )
        ) : null;

    return (
        <Box as={'header'} py={2} borderBottom={'1px solid {colors.gray.200}'}>
            <GridWrapper>
                {isSimplified ? (
                    <AppHeaderLogo />
                ) : (
                    <HStack as={GridItem} gridColumn={'1 / -1'} justify={'space-between'}>
                        <HStack gap={isMobileRes ? 0 : 'gutterWidth'}>
                            {isMobileRes ? (
                                <NavigationMobile
                                    menuLabel={t('menu.menuLabel')}
                                    closeLabel={t('menu.closeLabel')}
                                    user={user}
                                />
                            ) : null}
                            <AppHeaderLogo />
                            {!isMobileRes ? (
                                <Navigation triggerText={t('menu.planYourStay')} />
                            ) : null}
                        </HStack>
                        {isMobileSearchEngineVersion ? (
                            <Flex gap={4}>
                                <SearchEngineWrapper />
                                <HeaderRightSection />
                            </Flex>
                        ) : (
                            <>
                                <SearchEngineWrapper />
                                <HeaderRightSection />
                            </>
                        )}
                    </HStack>
                )}
            </GridWrapper>
        </Box>
    );
};
